import React from 'react';
import { css } from '@emotion/react';
import queryString from 'query-string';
import { useLocation } from '@reach/router';

import Header from '$/sections/Home/Header';

import { CopyToClipboardControl } from '$/components/ui/Input/Basics/Input';

import { logger } from '$/utils/logger';
import useRunOnce from '$/hooks/useRunOnce';
import { ChromeIcon, EdgeIcon, FirefoxIcon, SafariIcon } from '$/images/Icons';

import SEO from '../../SEO';
import UhOh from '../../ui/UhOh';
import { UhOhProps } from './types';
import { UhOhPage } from './UhOhPage';

export const NotSupported: React.FC<
  UhOhProps & {
    isEmbeddedPlayer?: boolean;
    url?: string;
    small?: boolean;
    inapp?: boolean;
  }
> = (props) => {
  const location = useLocation();
  const query = queryString.parse(location.search);
  const url = props.url ?? (query.url as string | undefined);
  const [mounted, setMounted] = React.useState(false);
  const ua = typeof window === 'undefined' ? '' : window.navigator.userAgent.toLowerCase();
  const isEdgeOnAndroid = /edga/.test(ua);

  useRunOnce(() => setMounted(true));

  const browserName = ua.includes('firefox')
    ? 'Firefox'
    : ua.includes('msie')
      ? 'Internet Explorer'
      : ua.includes('sedg')
        ? 'Microsoft Edge'
        : ua.includes('opera')
          ? 'Opera'
          : ua.includes('chrome')
            ? 'Chrome'
            : ua.includes('duckduckgo')
              ? 'DuckDuckGo'
              : ua.includes('safari')
                ? 'Safari'
                : 'this browser';

  logger.error('Unsupported browser:   ', ua);

  let errorMsg = `Almost there... `;
  if (props.inapp ?? query.inapp) {
    errorMsg = `Almost there, but please exit this app...`;
  } else if (
    typeof window !== 'undefined' &&
    window.supportedBrowsersWithMinimumVersion?.find((b) => b.name === browserName)
  ) {
    errorMsg = `Almost there but ${browserName} is too old...`;
  }

  return (
    <UhOhPage {...props}>
      <SEO title='Unsupported browser' />

      <div className='flex py-1e px-2e'>
        <Header linkTo='/not-supported' showUserMenu={false} />
      </div>

      <UhOh.Container>
        <UhOh.Title
          className={`
            ${props.small ? 'text-md' : 'text-xl'}
            ${props.small ? 'md:text-2xl' : 'md:text-3xl'}
            leading-normal
          `}
          css={css`
            line-height: 1;
          `}
        >
          {errorMsg}
          <br />
          copy this URL into a supported browser
        </UhOh.Title>
        <UhOh.Body className={props.small ? 'my-2e' : undefined}>
          {url && mounted && (
            <CopyToClipboardControl text={url} className='rounded-sm border text-black' />
          )}
        </UhOh.Body>
        <div
          className={props.small ? 'w-full' : ''}
          css={css`
            max-width: 525px;
          `}
        >
          <div
            className='flex justify-around w-full mt-2e'
            css={css`
              max-width: 525px;

              a {
                display: flex;
                flex-direction: column;
                align-items: center;

                svg {
                  margin-bottom: 0.25rem;
                  width: 4rem;
                  height: 4rem;
                }
              }
            `}
          >
            <a href='https://www.google.com/chrome/' target='_blank' rel='noreferrer noopener'>
              <ChromeIcon />
              Chrome
            </a>
            <a href='https://www.apple.com/safari/' target='_blank' rel='noreferrer noopener'>
              <SafariIcon />
              Safari
            </a>
            {!isEdgeOnAndroid && (
              <a href='https://www.microsoft.com/en-us/edge' target='_blank'>
                <EdgeIcon />
                Microsoft Edge
              </a>
            )}
            <a href='https://www.mozilla.org/en-US/firefox/new/' target='_blank'>
              <FirefoxIcon />
              Firefox
            </a>
          </div>
          {!props.small && (
            <UhOh.Note className='flex mt-5e'>
              <div>
                NOTE: If your business requires the use of Internet Explorer, switch to Microsoft
                Edge which{' '}
                <a
                  href='https://docs.microsoft.com/en-us/lifecycle/faq/internet-explorer-microsoft-edge'
                  target='_blank'
                >
                  supports Internet Explorer mode
                </a>{' '}
                for backward compatibility.
              </div>
            </UhOh.Note>
          )}
        </div>
      </UhOh.Container>
    </UhOhPage>
  );
};
